import {
  Shield, LayoutDashboard, Building2, GitBranch, Clock, Mail, BarChart3,
  Lock, KeyRound, Server, Globe,
} from "lucide-react";

export const NAV_LINKS = [
  { to: "/features", label: "Features" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/products", label: "Products" },
  { to: "/security", label: "Security" },
  { to: "/contact", label: "Contact" },
];

export const ROLES = [
  { key: "portal", title: "Staff Portal",  tagline: "Raise & track your tickets",
    accent: "from-cobalt-500 to-cyanx", icon: LayoutDashboard,
    perks: ["Passwordless OTP", "Kanban of your tickets", "Live SLA countdown"] },
  { key: "admin", title: "Admin Console", tagline: "Route, resolve, report",
    accent: "from-cobalt-600 to-cobalt-500", icon: Shield,
    perks: ["Auto-assignment engine", "Kanban + queue views", "SLA & escalation"] },
  { key: "super", title: "Superadmin",    tagline: "Multi-campus platform view",
    accent: "from-cobalt-700 to-cobalt-500", icon: Building2,
    perks: ["Tenant health monitor", "Cross-campus insights", "Branding & routing"] },
];

export const FEATURES = [
  { icon: GitBranch, title: "Auto-routing engine",
    desc: "Every ticket lands with the right technician in under 60 seconds — by category, load, and on-duty status. No manual triage.",
    detail: "Rules are configured per institute — category-to-team mapping, load-balancing across on-duty staff, and instant reassignment when someone goes offline. No dispatcher required." },
  { icon: Clock, title: "SLA & escalation",
    desc: "Per-category SLA timers with live countdowns. Breaches auto-escalate and notify before deadlines slip.",
    detail: "Each ticket category carries its own response and resolution targets. Timers run live on every card, warn ahead of breach, and escalate automatically to a supervisor if nobody acts in time." },
  { icon: LayoutDashboard, title: "Kanban + queue",
    desc: "Drag tickets across stages or work a filterable queue. Whatever fits how your team thinks.",
    detail: "Technicians can drag-and-drop through New → In Progress → Resolved, or switch to a dense, filterable queue view for high-volume triage. Both stay in sync in real time." },
  { icon: KeyRound, title: "Passwordless login",
    desc: "Staff and students sign in with a one-time email code. Admins can use a password — whichever the institute allows.",
    detail: "No password resets, no help-desk tickets about the help desk. A one-time code to the institute email is enough for requesters and technicians; admins can opt into password + OTP." },
  { icon: Mail, title: "Per-institute email",
    desc: "Each campus sends from its own mailbox — SMTP, Google Apps Script, or a provider API. Fully isolated.",
    detail: "Every tenant configures its own outbound mail — SMTP credentials, a Google Apps Script endpoint, or a transactional email API. Nothing is shared across institutes, including secrets." },
  { icon: BarChart3, title: "Live analytics",
    desc: "Raised-vs-resolved trends, SLA compliance, technician workload, and a live activity feed — updating as you work.",
    detail: "Dashboards track raised-vs-resolved trends, SLA compliance percentage, per-technician workload, and a live activity feed — so admins see the health of the desk without running a report." },
];

export const STEPS = [
  { n: "01", title: "Raise", desc: "A requester submits a ticket in seconds — category, priority, and details. Passwordless, from any device.",
    long: "The requester picks a category and priority, adds details and attachments, and submits — no account setup, no password. A one-time email code confirms identity if needed." },
  { n: "02", title: "Auto-route", desc: "The engine assigns it to the best-fit technician instantly and starts the SLA clock.",
    long: "The routing engine matches the ticket's category against on-duty technicians and current load, assigns it, and starts the SLA timer — all inside a second, with no dispatcher in the loop." },
  { n: "03", title: "Resolve", desc: "The technician works it through Kanban, comments keep the requester informed, and it closes with a full audit trail.",
    long: "The technician moves the ticket through Kanban stages, leaves comments the requester can see, and closes it once resolved. Every status change and comment is kept in an audit trail." },
];

export const PRODUCTS = [
  { name: "FortifyHub", tag: "Secure exams", url: "https://fortifyhub.net",
    desc: "India's secure online exam platform — 7 question types, 12+ Indian languages, proctoring, and live coding." },
  { name: "LMS Platform", tag: "Learning", url: "https://lms.fortifyhub.net",
    desc: "Courses, content delivery, and learner progress — tightly integrated with FortifyHub." },
  { name: "Placement Portal", tag: "Campus placements", url: "https://bmsit-placement.fortifyhub.net",
    desc: "Drive scheduling, eligibility gates, QR check-in, and placement analytics for institutions." },
  { name: "NexusGuard", tag: "Lab security", url: "https://arventiqlabs.com/nexusguard/index.html",
    desc: "Computer-lab security across 1,000+ Windows PCs — remote cleanup, policy enforcement, fleet monitoring." },
  { name: "Auditorium & Events", tag: "Campus events", url: "https://events.fortifyhub.net",
    desc: "Hall booking, event publishing, QR check-in and certificates — built for BMSIT&M and rolling out campus-wide." },
];

export const SECURITY = [
  { icon: Lock, label: "Encrypted secrets", desc: "Mail keys & passwords stored AES-encrypted, never in the browser.",
    detail: "Every credential an institute configures — mail keys, provider tokens, passwords — is encrypted at rest with AES and is never sent to or stored in the browser." },
  { icon: Shield, label: "Tenant isolation", desc: "Every query scoped to its institute. No campus sees another's data.",
    detail: "Every database query is scoped to its tenant at the data-access layer, not just in the UI — so a bug in one institute's view can't leak another institute's tickets." },
  { icon: Server, label: "Edge-hosted", desc: "Runs on Cloudflare's global edge with Supabase — fast and resilient.",
    detail: "The app runs on Cloudflare's global edge network with Supabase as the backing store, keeping latency low for campuses anywhere in India and resilience high under load." },
  { icon: Globe, label: "Data in India", desc: "DPDP-aligned data residency, built India-first by Arventiq Labs.",
    detail: "Data residency follows India's DPDP Act 2023 from the ground up — this isn't a retrofit, it's how Arventiq Labs builds every product." },
];

export const CONTACT = {
  phone: "+91 70287 00361",
  email: "beerappa@fortifyhub.net",
  office: "BMS Innovation Centre (BICEP), CV Raman Block, 2F, BMSIT&M, Yelahanka, Bangalore — 560119",
  website: "fortifyhub.net",
  services: [
    "Solution — Ticketing OS",
    "FortifyHub — Exam Platform",
    "LMS Platform",
    "Placement Portal",
    "Auditorium & Events",
    "Cybersecurity Consulting / Pen Testing",
    "Custom Software Development",
    "Other",
  ],
};
